import { useQuery } from "@tanstack/react-query"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@workspace/web-ui/components/tabs"
import { encountersKeys, encountersQueryOptions } from "../api/queries"
import { useState, useEffect, useMemo } from "react"
import { ChevronRight } from "lucide-react"
import { ChevronLeft } from "lucide-react"
import { EncounterFilters } from "../components/encounter-filter"
import { EncounterCard } from "../components/encounter-card"
import { EncounterFilterDate, EncounterFilterStatus, FormattedEncounter } from "../types"
import { Button } from "@workspace/web-ui/components/button"
import { EncounterDetails } from "../components/encounter-details"
import { getRouteApi, linkOptions, useNavigate, useParams } from "@tanstack/react-router"
import { useDebounce } from "~/hooks/use-debounce"
import { getQueryClient } from "~/providers/query.provider"
import { UrgentCareEncounterCard } from "../components/urgentcare-card"
import { getApiProgram } from "~/lib/utils"
import { QUICK_ACTIONS_POLLING_INTERVAL } from "../../quick-actions/types/constant";


// Skeleton loader for encounter list
function EncounterListSkeleton() {
    return (
        <div className="flex flex-col gap-4 w-full">
            {[...Array(5)].map((_, i) => (
                <div
                    key={i}
                    className="animate-pulse bg-gray-200 rounded h-24 w-full"
                />
            ))}
        </div>
    );
}

export const encountersLinkOptions = (program = 'weight-loss', tab = 'all') => linkOptions({
    to: '/dashboard/$program/encounters/$tab',
    params: { program, tab }
})

export function EncountersPage() {
    const routeApi = getRouteApi("/(dashboard)/dashboard/$program");
    const { program } = routeApi.useParams();

    const queryClient = getQueryClient()
    const { tab } = useParams({ strict: false })
    const navigate = useNavigate()
    const [currentPage, setCurrentPage] = useState(1);
    const [isMobile, setIsMobile] = useState(false);
    const [searchValue, setSearchValue] = useState("")
    const debouncedSearchValue = useDebounce<string>(searchValue, 400);
    const [filter, setFilter] = useState("");
    const [date, setDate] = useState("");
    const [status, setStatus] = useState<EncounterFilterStatus>("unresolved");
    const [isEncounterOpen, setEncounterOpen] = useState(false);
    const [selectedEncounter, setSelectedEncounter] = useState<FormattedEncounter | null>(null)
    const pageSize = 10;
    const skip = (currentPage - 1) * pageSize;


    const renderEncounterCard = (encounter: FormattedEncounter) => {
        if (program === "urgent-care") {
            return (
                <UrgentCareEncounterCard
                    key={encounter.id}
                    item={encounter}
                    program={program ?? ""}
                    selectedEncounter={selectedEncounter}
                    setSelectedEncounter={setSelectedEncounter}
                    onOpenChange={setEncounterOpen}
                />
            );
        }
        // fallback to weight-loss or default
        return (
            <EncounterCard
                key={encounter.id}
                item={encounter}
                program={program ?? ""}
                selectedEncounter={selectedEncounter}
                setSelectedEncounter={setSelectedEncounter}
                onOpenChange={setEncounterOpen}
            />
        );
    };



    // Detect viewport width for responsive adjustments
    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 640);
        };

        // Check on initial render
        checkScreenSize();

        // Add event listener for window resize
        window.addEventListener("resize", checkScreenSize);

        // Clean up event listener
        return () => window.removeEventListener("resize", checkScreenSize);
    }, []);


    // Fetch only the current page's data
    // Fetch only the current page's data
    const { data, isLoading, isPlaceholderData, error } = useQuery(
        {
            ...encountersQueryOptions({ limit: pageSize, skip, program: getApiProgram(program), type: tab === "all" ? undefined : tab, date_filter: date === "all" ? "all" : date as EncounterFilterDate, status: status === "all" ? "all" : status as EncounterFilterStatus, patient_search: debouncedSearchValue }),
            refetchInterval: QUICK_ACTIONS_POLLING_INTERVAL
        }
    );

    // Success and error logging
    useEffect(() => {
        if (data) { }
    }, [data]);

    useEffect(() => {
        if (error) { }
    }, [error, pageSize, skip, program, tab, date, status, debouncedSearchValue]);


    // Always use "weight_loss" for the backend query, even for "urgent-care"
    // const resolvedProgram = (program === "urgent-care" || program === "weight-loss") 
    //   ? "weight_loss" 
    //   : program.replace("-", "_");

    // const { data, isLoading, isPlaceholderData } = useQuery(
    //     encountersQueryOptions({
    //         limit: pageSize,
    //         skip,
    //         program: resolvedProgram,
    //         type: tab === "all" ? undefined : tab,
    //         date_filter: date === "all" ? "all" : date as EncounterFilterDate,
    //         filter: filter === "all" ? "all" : filter as EncounterFilter,
    //         status: status === "all" ? "all" : status as EncounterFilterStatus,
    //         search: debouncedSearchValue
    //     })
    // );


    // Prefetch the next page!
    useEffect(() => {
        if (!isPlaceholderData && data?.hasMore) {
            queryClient.prefetchQuery({
                queryKey: encountersKeys.encounters({
                    limit: pageSize,
                    skip: skip + pageSize,

                    type: tab === "all" ? undefined : tab, date_filter: date === "all" ? "all" : date as EncounterFilterDate, status: status === "all" ? "all" : status as EncounterFilterStatus, patient_search: debouncedSearchValue
                }),
                queryFn: () => encountersQueryOptions({
                    limit: pageSize,
                    skip: skip + pageSize,

                    type: tab === "all" ? undefined : tab, date_filter: date === "all" ? "all" : date as EncounterFilterDate, status: status === "all" ? "all" : status as EncounterFilterStatus, patient_search: debouncedSearchValue
                }),
            })
        }
    }, [data, isPlaceholderData, debouncedSearchValue, filter, date, status, tab, pageSize, skip])

    // Defensive fallback for data
    const rawEncounters = Array.isArray(data?.encounters) ? data.encounters : [];
    const total = typeof data?.totalCount === "number" ? data.totalCount : 0;

    const encounters = useMemo(() => {
        let result = [...rawEncounters];

        // Sort by date
        if (filter === "old") {
            result.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        } else if (filter === "new") {
            result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        }

        return result;
    }, [rawEncounters, filter]);
    // Reset to first page and clear selected encounter when filters/search change
    useEffect(() => {
        setCurrentPage(1);
        setSelectedEncounter(null);
    }, [tab, date, status, debouncedSearchValue]);

    const totalPages = Math.ceil(total / pageSize) || 1;
    const startIndex = total === 0 ? 0 : (skip + 1);
    const endIndex = total === 0 ? 0 : (skip + encounters.length);

    const handleTabChange = (tab: string) => {
        navigate(encountersLinkOptions(program, tab))
        setSelectedEncounter(null);
        setCurrentPage(1); // Reset to first page on tab change
    };

    const handlePageChange = (page: number) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    return (
        <section
            aria-label="EncountersPage"
            className="w-full max-w-[1440px] mx-auto flex flex-col px-4 sm:px-6 md:px-8 lg:px-12 gap-4 sm:gap-6 py-4 sm:py-6 hide-scrollbar"
        >
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold">
                {isMobile ? "Encounters" : "All Patient Encounters"}
            </h2>

            <Tabs
                defaultValue="all"
                value={tab}
                onValueChange={handleTabChange}
            >
                <div className="w-full flex overflow-x-auto pb-1 sm:pb-2">
                    <TabsList className="flex border-b no-scrollbar bg-transparent px-0 py-0 sm:py-1 whitespace-nowrap">
                        <TabsTrigger
                            value="all"
                            className="tab-trigger flex-shrink-0 text-xs sm:text-sm md:text-base whitespace-nowrap py-2"
                        >
                            All Encounters
                        </TabsTrigger>
                        <TabsTrigger
                            value="consultation"
                            className="tab-trigger flex-shrink-0 text-xs sm:text-sm md:text-base whitespace-nowrap py-2"
                        >
                            Consultations
                        </TabsTrigger>
                        <TabsTrigger
                            value="appointment"
                            className="tab-trigger flex-shrink-0 text-xs sm:text-sm md:text-base whitespace-nowrap py-2"
                        >
                            Appointments
                        </TabsTrigger>
                        <TabsTrigger
                            value="refill"
                            className="tab-trigger flex-shrink-0 text-xs sm:text-sm md:text-base whitespace-nowrap py-2"
                        >
                            Medications Refill
                        </TabsTrigger>
                    </TabsList>
                </div>

                {tab && (<TabsContent value={tab} className="mt-2 sm:mt-4 w-full sm:w-full">
                    <div className="flex flex-col w-full gap-[32px] items-start">
                        <div className="w-full">
                            <EncounterFilters
                                searchValue={searchValue}
                                setSearchValue={setSearchValue}
                                setDate={setDate}
                                date={date}
                                setFilter={setFilter}
                                filter={filter}
                                status={status}
                                setStatus={setStatus}
                            />
                        </div>
                        {isLoading ? (
                            <EncounterListSkeleton />
                        ) : encounters.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 w-full sm:w-full h-[100vh] gap-4">
                                <div className="flex w-full sm:w-[80%] flex-col gap-4">
                                    <div className="overflow-y-auto flex flex-col gap-4 hide-scrollbar max-h-[calc(100vh-16rem)] pr-2">
                                        {encounters.map(renderEncounterCard)}
                                    </div>

                                    {/* Pagination UI - Outside scroll area */}
                                    <div className="flex items-center justify-between border-t border-gray-200 px-2 py-3 w-full bg-white">
                                        <div className="text-sm text-gray-700">
                                            Showing <span className="font-medium">{startIndex}</span> to{" "}
                                            <span className="font-medium">{endIndex}</span> of{" "}
                                            <span className="font-medium">{total}</span> Results
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                disabled={currentPage === 1 || total === 0}
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                className="h-8 w-8 rounded cursor-pointer"
                                                aria-label="Previous page"
                                            >
                                                <ChevronLeft className="h-4 w-4 cursor-pointer" />
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                disabled={currentPage === totalPages || total === 0}
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                className="h-8 w-8 rounded cursor-pointer"
                                                aria-label="Next page"
                                            >
                                                <ChevronRight className="h-4 w-4 cursor-pointer" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                                {selectedEncounter && (
                                    <div className="sticky top-4 h-fit max-h-[calc(100vh-12rem)] overflow-y-auto hide-scrollbar">
                                        <EncounterDetails encounter={selectedEncounter} onOpenChange={setEncounterOpen} open={isEncounterOpen} />
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="flex flex-col gap-3 justify-center items-center w-full">
                                <h2 className="text-lg font-medium">No Encounters found</h2>
                                <p className="text-sm text-gray-500">When encounters are available, they will appear here</p>
                            </div>
                        )}
                    </div>
                </TabsContent>)}
            </Tabs>
        </section>
    );
}